import { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import SessionTimeoutModal from '../components/SessionTimeoutModal/SessionTimeoutModal';

const AuthContext = createContext(null);

const STORAGE_KEY      = 'mdm_auth';
const REFRESH_BEFORE_MS = 60_000;       // refresh 60 s before expiry
const INACTIVITY_MS     = 30 * 60_000;  // show warning after 30 min idle
const IDLE_CHECK_MS     = 30_000;       // poll every 30 s

function jwtExpiry(token) {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

function makeAuthData(token, extra = {}) {
  return {
    token,
    expiresAt: jwtExpiry(token) ?? (Date.now() + 8 * 60 * 60_000),
    ...extra,
  };
}

function loadStored() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const stored = JSON.parse(raw);
    if (stored.expiresAt && Date.now() > stored.expiresAt) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return stored;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }) {
  const [auth, setAuth]                   = useState(loadStored);
  const [sessionWarning, setSessionWarning] = useState(false);
  const timerRef      = useRef(null);
  const lastActivityRef = useRef(Date.now());

  const logout = useCallback(() => {
    clearTimeout(timerRef.current);
    localStorage.removeItem(STORAGE_KEY);
    setAuth(null);
  }, []);

  const applyAuth = useCallback((data) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    setAuth(data);
  }, []);

  const refreshToken = useCallback(async (currentAuth) => {
    const base = process.env.REACT_APP_MDM_BASE || '/mdm';
    const tokenExpired = () => Date.now() >= currentAuth.expiresAt;
    const scheduleRetry = () => {
      timerRef.current = setTimeout(() => refreshToken(currentAuth), 2 * 60_000);
    };
    try {
      const res = await fetch(`${base}/api/v3/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${currentAuth.token}`,
        },
        body: JSON.stringify({ request: { token: currentAuth.token } }),
      });
      if (!res.ok) {
        tokenExpired() ? logout() : scheduleRetry();
        return;
      }
      const json = await res.json();
      if (json.code !== 200 || !json.data?.accessToken) {
        tokenExpired() ? logout() : scheduleRetry();
        return;
      }
      applyAuth(makeAuthData(json.data.accessToken, { user: currentAuth.user }));
    } catch {
      tokenExpired() ? logout() : scheduleRetry();
    }
  }, [logout, applyAuth]);

  const continueSession = useCallback(() => {
    lastActivityRef.current = Date.now();
    setSessionWarning(false);
  }, []);

  // Track user activity — reset idle clock on any interaction
  useEffect(() => {
    if (!auth) return;
    const onActivity = () => { lastActivityRef.current = Date.now(); };
    const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll'];
    events.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));

    const checker = setInterval(() => {
      if (Date.now() - lastActivityRef.current >= INACTIVITY_MS) {
        setSessionWarning(true);
      }
    }, IDLE_CHECK_MS);

    return () => {
      events.forEach((e) => window.removeEventListener(e, onActivity));
      clearInterval(checker);
    };
  }, [auth]);

  // Schedule refresh 60 s before expiry; fall back to logout if refresh fails
  useEffect(() => {
    if (!auth?.expiresAt) return;
    const delay = auth.expiresAt - Date.now() - REFRESH_BEFORE_MS;
    if (delay <= 0) {
      // Less than 60 s left — try to refresh immediately
      refreshToken(auth);
      return;
    }
    timerRef.current = setTimeout(() => refreshToken(auth), delay);
    return () => clearTimeout(timerRef.current);
  }, [auth, refreshToken]);

  const login = useCallback(async (username, password) => {
    const base = process.env.REACT_APP_MDM_BASE || '/mdm';
    const res = await fetch(`${base}/api/v3/auth/signin`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ request: { username, password } }),
    });

    const text = await res.text();
    let json;
    try {
      json = JSON.parse(text);
    } catch {
      throw new Error(`Server error: ${text || res.statusText}`);
    }

    if (!res.ok || json.code !== 200) {
      throw new Error(json.message || 'Invalid credentials');
    }

    applyAuth(makeAuthData(json.data.accessToken, {
      user: {
        fullName: json.data.fullName,
        username: json.data.username,
        roleId:   json.data.roleId,
      },
    }));
  }, [applyAuth]);

  return (
    <AuthContext.Provider value={{ auth, login, logout }}>
      {children}
      {auth && sessionWarning && (
        <SessionTimeoutModal onContinue={continueSession} onLogout={logout} />
      )}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
